// Sprint-017 E7 (project tasks) — the AUTHORITATIVE, verbatim copy for the
// editable checklist, assign flow, "who's helping" sharing, and the
// de-identified assignee surface.
//
// Every string below is lifted VERBATIM from the UX copy dictionary
// (`specs/ux-project-tasks-flows-2026-07-11.md` §8) and is CD-signed-off
// (`specs/cd-signoff-project-tasks-2026-07-11.md`). The banned-words list
// — role / permission / assign / collaborator / share / private / public /
// member / manager / admin / revoke / grant — appears in NO user-facing
// label, empty state, confirm, snackbar, or notification here. The sharing
// concept is voiced as "letting someone help"; the assign concept as
// "Who's doing …?"; clearing as "Nobody yet"; leaving as "Leave this
// project". Do not paraphrase — the register is deliberate.

// ── Projects list / ownership (Flow 1) ─────────────────────────────────
/** More → Projects sub-label for a member/manager (their own + helping). */
export const PROJECTS_ROW_SUB_MEMBER = "Your lists, and anything you're helping with.";
/** More → Projects sub-label for an admin (existing, all-projects reach). */
export const PROJECTS_ROW_SUB_ADMIN = "What's underway on the property.";
/** Section heading — projects the caller owns. */
export const PROJECTS_SECTION_YOURS = "Yours";
/** Section heading — projects shared with the caller. */
export const PROJECTS_SECTION_HELPING = "Helping out";
/** Create CTA (every role). */
export const START_PROJECT_LABEL = "+ Start a project";
/** Empty-state title (no projects yet). */
export const PROJECTS_EMPTY_TITLE = "No projects yet.";
/** Empty-state body. */
export const PROJECTS_EMPTY_BODY = "Start one to keep your own list of what needs doing.";
/** Quiet ownership attribution on a NON-owned card (admin's flat view). */
export function startedByLabel(name: string): string {
  return `Started by ${name}`;
}
/** Snackbar after creating a project. */
export function projectStartedMessage(name: string): string {
  return `Started ${name}.`;
}

// ── Editable checklist (Flow 2) ────────────────────────────────────────
/** Checklist section heading (replaces the display-only "Still to do"). */
export const CHECKLIST_HEADING = "The list";
/** Mode toggle — view → edit. */
export const CHECKLIST_EDIT_LABEL = "Edit";
/** Mode toggle — edit → view. */
export const CHECKLIST_DONE_LABEL = "Done";
/** Pinned add row (both modes). */
export const ADD_TASK_LABEL = "+ Add something to the list";
/** Placeholder for the inline add-task field. */
export const ADD_TASK_PLACEHOLDER = "Add something to the list";
/** Empty-checklist title (just-created project). */
export const CHECKLIST_EMPTY_TITLE = "Nothing on the list yet.";
/** Empty-checklist body. */
export const CHECKLIST_EMPTY_BODY = "Add the first thing that needs doing.";
/** Roll-up pill grammar — "{n} of {m} done". */
export function rollupLabel(done: number, total: number): string {
  return `${done} of ${total} done`;
}
/** Delete confirm (only shown when the task is assigned OR done — Q3). */
export function deleteTaskConfirmTitle(label: string): string {
  return `Remove "${label}"? This can't be undone.`;
}
/** Undo snackbar after an immediate (unassigned/undone) delete. */
export function taskRemovedMessage(label: string): string {
  return `Removed "${label}".`;
}
/** Reorder handle accessible name (edit mode). */
export function reorderTaskAriaLabel(label: string): string {
  return `Reorder ${label}`;
}
/** Delete control accessible name (edit mode). */
export function deleteTaskAriaLabel(label: string): string {
  return `Remove ${label}`;
}
/** Keyboard-reorder live-region announcement. */
export function movedToAnnouncement(position: number, total: number): string {
  return `Moved to ${position} of ${total}`;
}

// ── Assign (Flow 3 — admin/manager only) ───────────────────────────────
/** Safety toggle row inside the picker + the per-row edit-mode toggle. */
export const SAFETY_TASK_LABEL = "Safety task";
/** Clear-assignee row (never the word "unassign"). */
export const NOBODY_YET_LABEL = "Nobody yet";
/** Static assignee chip's accessible name when the task IS assigned but
 * this view has no name for that person (a shared-in helper can't read the
 * project's helper list; see TD-044). */
export const SOMEONE_ASSIGNED_LABEL = "Someone's on it";
/** Assign chip accessible name when interactive (admin/manager). */
export function whosDoingAriaLabel(label: string): string {
  return `Who's doing "${label}"?`;
}
/** Snackbar — first assign. */
export function assignedMessage(name: string): string {
  return `${name} will see this on their list.`;
}
/** Snackbar — reassign to a different person. */
export function reassignedMessage(name: string): string {
  return `Now ${name} will see this on their list.`;
}
/** Snackbar — cleared back to "Up for grabs". */
export const CLEARED_ASSIGNEE_MESSAGE = "Back up for grabs.";

// ── Sharing / "who's helping" (Flow 4 — owner only) ────────────────────
/** Section heading (owner view). */
export const WHOS_HELPING_HEADING = "Who's helping";
/** Add-helper row. */
export const LET_SOMEONE_HELP_LABEL = "+ Let someone help";
/** Add-helper picker title (multi-select). */
export const WHO_CAN_HELP_TITLE = "Who can help with this?";
/** Remove control on a helper row. */
export const REMOVE_HELPER_LABEL = "Remove";
/** Commit label for the multi-select add-helper picker. */
export const HELPERS_DONE_LABEL = "Done";
/** Un-share confirm. */
export function removeHelperConfirmTitle(name: string, project: string): string {
  return `Take ${name} off ${project}? Anything they added or checked off stays.`;
}
/** Un-share snackbar. */
export function helperRemovedMessage(name: string, project: string): string {
  return `Took ${name} off ${project}.`;
}
/** Helper-added snackbar. */
export function helperAddedMessage(name: string): string {
  return `${name} can help now.`;
}
/** Helper's quiet self-remove control. */
export const LEAVE_PROJECT_LABEL = "Leave this project";
/** Leave confirm. */
export function leaveProjectConfirmTitle(project: string): string {
  return `Leave ${project}? You can be added back later. Anything you added stays.`;
}
/** Leave snackbar. */
export function leftProjectMessage(project: string): string {
  return `You left ${project}.`;
}

// ── Notifications (Flow 6 — assignee-facing, de-identified) ────────────
/** In-app / push new-task notification — task LABEL only, never a project
 * name. (ADR-003 §14 display map: `PROJECT_TASK_ASSIGNED → "New task for
 * you"`; the client renders the label, never the raw enum.) */
export function newTaskNotification(label: string): string {
  return `You've got a new task: ${label}.`;
}
/** ADR-003 §14 / ADR-002 §14 enum display map entry — the human label a
 * `ReminderKind.PROJECT_TASK_ASSIGNED` renders as (never the raw enum). */
export const PROJECT_TASK_ASSIGNED_DISPLAY = "New task for you";
